<template>
  <!-- The order details a new order asks for besides its commercial terms: orderer,
       customer answers, notifications, alarm setup, delivery, contact persons,
       accounts, comment and the alarm plan. Same fields, rules and order as the
       order form (components/modal/admin_order_alarm.vue) - change both together.
       Used by the redraw flow (agreement_redraw.vue), whose form already has the
       seller, SIM card, alarm central, category, insurance, products and terms. -->
  <ValidationObserver ref="orderDetailsObserver" tag="div">
    <div class="flex flex-wrap mt-4">
      <div class="w-full text-gray-600 text-md font-bold px-2 pb-3 font-serif">
        {{ $t('admin_order_alarm.orderer_info') }}
      </div>
      <div class="w-full md:w-1/3 px-2 py-2">
        <BaseInput v-model="form.customer_info.contact_name" vid="od-contact-name" type="text" :field_name="$t('admin_order_alarm.name')" rules="required" />
      </div>
      <div class="w-full md:w-1/3 px-2 py-2">
        <BaseInput v-model="form.customer_info.contact_email" vid="od-contact-email" type="text" :field_name="$t('admin_order_alarm.email')" rules="required|email" />
      </div>
      <div class="w-full md:w-1/3 px-2 py-2">
        <BaseInput v-model="form.customer_info.contact_phone" vid="od-contact-phone" type="text" :field_name="$t('admin_order_alarm.phone')" rules="required" />
      </div>
      <div class="w-full md:w-1/2 px-2 py-2">
        <BaseTextarea rows="3" v-model="form.customer_info.company_name" vid="od-company-name" :field_name="$t('admin_order_alarm.company_name')" rules="required" />
      </div>
      <div class="w-full md:w-1/2 px-2 py-2">
        <BaseTextarea rows="3" v-model="form.customer_info.invoice_detail" vid="od-invoice-detail" :field_name="$t('admin_order_alarm.invoice_address')" rules="required" />
      </div>
    </div>

    <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
    <div class="flex flex-wrap mt-4">
      <div class="w-full text-gray-600 text-md font-bold px-2 pb-3 font-serif">
        {{ $t('admin_order_alarm.general_questions') }}
      </div>
      <div class="w-full md:w-1/2 px-2 py-2" v-for="question in generalQuestions" :key="question.field">
        <ValidationProvider :name="$t(question.label)" :vid="`od-${question.field}`" rules="required" v-slot="{ classes, errors }">
          <div class="input-validate" :class="classes">
            <label class="text-gray-600 text-xs pb-2 font-serif">{{ $t(question.label) }}<b class="text-red-500 ml-1">*</b></label>
            <input type="text" v-model="form.customer_info[question.field]" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 pt-1 pb-1" />
            <span>{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
    </div>

    <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
    <div class="flex flex-wrap mt-4">
      <div class="w-full text-gray-600 text-md font-bold px-2 pb-3 font-serif">
        {{ $t('admin_order_alarm.notifications_section') }}
      </div>
      <div class="w-full px-2 pb-1">
        <label class="inline-flex items-center text-sm text-gray-800 cursor-pointer">
          <input type="checkbox" class="form-checkbox h-4 w-4" v-model="form.notify_general" />
          <span class="ml-2 font-semibold">{{ $t('admin_order_alarm.notifications_general') }}</span>
        </label>
        <p class="text-xs italic text-gray-500 ml-6">{{ $t('admin_order_alarm.notifications_general_desc') }}</p>
      </div>
      <div class="w-full px-2 py-2" v-if="form.notify_general">
        <div class="w-full md:w-2/3 flex flex-wrap mt-1" v-for="(contact, i) in notificationContacts" :key="`od-notification-${i}`">
          <div class="w-5/12 pr-1">
            <ValidationProvider :name="`${$t('admin_order_alarm.email')} ${i + 1}`" :vid="`od-notification-email-${i}`" rules="email" v-slot="{ classes, errors }">
              <div class="input-validate" :class="classes">
                <input v-model.trim="contact.email" type="text" :placeholder="$t('admin_order_alarm.email')" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 py-2" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
          <div class="w-5/12 px-1">
            <ValidationProvider :name="`${$t('admin_order_alarm.phone')} ${i + 1}`" :vid="`od-notification-phone-${i}`" rules="sms_msisdn" :customMessages="{ sms_msisdn: $t('admin_order_alarm.sms_number_format') }" v-slot="{ classes, errors }">
              <div class="input-validate" :class="classes">
                <input v-model.trim="contact.msisdn" type="text" @keypress="isNumber($event)" maxlength="15" :placeholder="$t('admin_order_alarm.phone')" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 py-2" />
                <span>{{ errors[0] }}</span>
              </div>
            </ValidationProvider>
          </div>
          <button class="w-1/12 text-red-500 pl-1" v-if="notificationContacts.length > 1" @click.prevent="removeNotificationContact(i)"><BaseIcon icon="minus-circle" /></button>
        </div>
        <button class="btn-blue-outline mt-2" @click.prevent="addNotificationContact()">{{ $t('admin_order_alarm.add') }} <BaseIcon icon="plus" class="ml-1" /></button>
        <!-- answers written before the structured fields existed -->
        <p v-if="form.customer_info.email_sms_detail" class="text-xs italic text-gray-500 mt-2">
          {{ $t('admin_order_alarm.legacy_free_text') }}: {{ form.customer_info.email_sms_detail }}
        </p>
      </div>
      <div class="w-full px-2 pt-2 pb-1">
        <label class="inline-flex items-center text-sm text-gray-800 cursor-pointer">
          <input type="checkbox" class="form-checkbox h-4 w-4" v-model="form.notify_individual" />
          <span class="ml-2 font-semibold">{{ $t('admin_order_alarm.notifications_individual') }}</span>
        </label>
        <p class="text-xs italic text-gray-500 ml-6">{{ $t('admin_order_alarm.notifications_individual_desc') }}</p>
      </div>
      <div class="w-full px-2 py-2" v-if="form.notify_individual">
        <BaseTextarea rows="5" v-model="form.notification_individual_detail" vid="od-notification-individual" :field_name="$t('admin_order_alarm.notifications_individual')" />
        <p class="text-xs italic text-gray-500 mt-1">{{ $t('admin_order_alarm.notifications_individual_hint') }}</p>
      </div>
    </div>

    <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
    <div class="flex flex-wrap mt-4">
      <div class="w-full text-gray-600 text-md font-bold px-2 pb-3 font-serif">
        {{ $t('admin_order_alarm.alarm_basis') }}
      </div>
      <div class="w-full md:w-1/2 px-2 py-2">
        <ValidationProvider :name="$t('admin_order_alarm.total_users')" vid="od-total-user" rules="required|numeric" v-slot="{ classes, errors }">
          <div class="input-validate" :class="classes">
            <label class="text-gray-600 text-xs pb-2 font-serif">{{ $t('admin_order_alarm.total_users') }}<b class="text-red-500 ml-1">*</b></label>
            <input type="text" v-model="form.customer_info.total_user" @keypress="isNumber($event)" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 pt-1 pb-1" />
            <span>{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
      <div class="w-full md:w-1/2 px-2 py-2">
        <ValidationProvider :name="$t('admin_order_alarm.work_tasks')" vid="od-work-task" rules="required" v-slot="{ classes, errors }">
          <div class="input-validate" :class="classes">
            <label class="text-gray-600 text-xs pb-2 font-serif">{{ $t('admin_order_alarm.work_tasks') }}<b class="text-red-500 ml-1">*</b></label>
            <textarea rows="4" v-model="form.customer_info.work_task" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 pt-1 pb-1"></textarea>
            <span>{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
      <div class="w-full md:w-1/2 px-2 py-2">
        <ValidationProvider :name="$t('admin_order_alarm.workplace_location')" vid="od-working-place" rules="required" v-slot="{ classes, errors }">
          <div class="input-validate" :class="classes">
            <label class="text-gray-600 text-xs pb-2 font-serif">{{ $t('admin_order_alarm.workplace_location') }}<b class="text-red-500 ml-1">*</b></label>
            <input type="text" v-model="form.customer_info.working_place" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 pt-1 pb-1" />
            <span>{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
      <div class="w-full md:w-1/2 px-2 py-2">
        <ValidationProvider :name="$t('admin_order_alarm.risk_assessment')" vid="od-risk-detail" rules="required" v-slot="{ classes, errors }">
          <div class="input-validate" :class="classes">
            <label class="text-gray-600 text-xs pb-2 font-serif">{{ $t('admin_order_alarm.risk_assessment') }}<b class="text-red-500 ml-1">*</b></label>
            <input type="text" v-model="form.customer_info.risk_detail" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-2 border-gray-300 transition duration-500 px-3 pt-1 pb-1" />
            <span>{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>
      <div class="w-full md:w-1/2 px-2 py-2">
        <label class="text-gray-600 text-xs pb-2 font-serif">{{ $t('admin_order_alarm.other_info') }}</label>
        <textarea rows="3" v-model="form.customer_info.other_info" class="bg-gray-100 rounded w-full text-sm text-gray-900 focus:outline-none border-b-4 border-gray-300 transition duration-500 px-3 pt-2 pb-2" />
      </div>
    </div>

    <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
    <div class="flex flex-wrap mt-4">
      <div class="w-full md:w-1/3 px-2 py-2">
        <BaseSwitch v-model="form.direct_dispatch" :field_name="$t('alarm_pdf.direct_dispatch')" />
      </div>
    </div>
    <!-- with guard dispatch on, the external contractor is captured like on the order form -->
    <div class="flex flex-wrap" v-if="form.direct_dispatch">
      <div class="w-full text-xs italic px-2 mb-1">
        {{ $t('alarm_create.external_contractor') }}
      </div>
      <div class="w-full md:w-1/3 px-2 py-2">
        <BaseInput v-model="form.security_company" vid="od-security-company" type="text" :field_name="$t('alarm_create.name')" rules="required" />
      </div>
      <div class="w-full md:w-1/3 px-2 py-2">
        <BaseInput v-model="form.security_company_phone" vid="od-security-company-phone" type="text" :field_name="$t('alarm_create.contact_number')" rules="required" />
      </div>
      <div class="w-full md:w-1/3 px-2 py-2">
        <BaseInput v-model="form.security_company_object_id" vid="od-security-company-object" type="text" :field_name="$t('alarm_create.object_id')" />
      </div>
    </div>

    <div class="flex flex-wrap mt-4">
      <div class="w-full text-xs italic text-gray-500 px-2 pb-2">
        {{ $t('admin_order_alarm.alarm_options_hint') }}
      </div>
      <div class="w-full md:w-1/3 px-2 py-2" v-for="option in alarmOptionCheckboxes" :key="option.field">
        <label class="inline-flex items-center text-sm text-gray-800 cursor-pointer">
          <input type="checkbox" class="form-checkbox h-4 w-4" v-model="form[option.field]" />
          <span class="ml-2 font-bold text-gray-600 font-serif">{{ option.label }}</span>
        </label>
      </div>
    </div>
    <div class="flex flex-wrap mt-4">
      <div class="w-full md:w-1/3 px-2 py-2">
        <BaseSelect v-model="form.delivery_method" vid="od-delivery-method" :options="deliveryMethodOptions" nameSelector="name" optionSelector="value" :field_name="$t('admin_order_alarm.delivery_method')" rules="required" />
      </div>
      <div class="w-full md:w-1/3 px-2 py-2" v-if="isDeliveryToRecipient">
        <BaseInput v-model="form.direct_shipping_address" vid="od-shipping-address" type="text" :field_name="$t('admin_order_alarm.direct_shipping_address')" rules="required" />
        <p class="text-xs italic text-gray-500 mt-1">{{ $t('admin_order_alarm.delivery_address_hint') }}</p>
      </div>
      <div class="w-full md:w-1/3 px-2 py-2" v-if="isDeliveryToRecipient">
        <BaseInput v-model="form.delivery_tracking_link" vid="od-tracking-link" type="text" :field_name="$t('admin_order_alarm.delivery_tracking_link')" />
        <p class="text-xs italic text-gray-500 mt-1">{{ $t('admin_order_alarm.delivery_tracking_link_hint') }}</p>
      </div>
    </div>

    <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
    <div class="flex flex-wrap mt-4">
      <div class="w-full text-gray-600 text-sm font-bold px-2 pb-1 font-serif">
        {{ $t('admin_order_alarm.contact_persons') }}
      </div>
      <div class="w-full text-xs italic text-gray-500 px-2 pb-2">
        {{ $t('admin_order_alarm.contact_persons_hint') }}
      </div>
      <!-- Safe-4: the contact persons are called around the clock, so the time fields are locked -->
      <div class="w-full px-2 text-xs italic mb-1" v-if="isSafe4">
        {{ $t("safe4_form.around_clock") }}
      </div>
      <div class="w-full flex flex-wrap" v-for="(c, i) in form.contact_persons" :key="`od-contact-${i}`">
        <div class="w-full md:w-3/12 px-2 py-1">
          <BaseInput v-model="c.name" :vid="`od-contact-name-${i}`" type="text" :rules="contactRules(i === 0, 50)" :maxlength="isSafe4 ? 50 : undefined" :field_name="i === 0 ? $t('alarm_create.name') : ''" :placeholder="$t('alarm_create.name')" />
        </div>
        <div class="w-full md:w-3/12 px-2 py-1">
          <BaseInput v-model="c.function" :vid="`od-contact-function-${i}`" type="text" :rules="contactRules(i === 0, 40)" :maxlength="isSafe4 ? 40 : undefined" :field_name="i === 0 ? $t('alarm_create.function') : ''" :placeholder="$t('alarm_create.function')" />
        </div>
        <div class="w-full md:w-3/12 px-2 py-1">
          <BasePhoneInput v-model="c.phone" :vid="'contact-phone-' + i" :show_country="isSafe4" :rules="safe4PhoneRules(form.contact_persons, i, isSafe4, i === 0)" :maxlength="isSafe4 ? 20 : undefined" :field_name="i === 0 ? $t('alarm_create.number') : ''" :placeholder="$t('alarm_create.number')" />
        </div>
        <div class="w-full md:w-1/12 px-2 py-1">
          <BaseInput v-model="c.weekday" type="text" :disabled="isSafe4" :field_name="i === 0 ? $t('alarm_create.weekday') : ''" :placeholder="$t('alarm_create.weekday')" />
        </div>
        <div class="w-full md:w-1/12 px-2 py-1">
          <BaseInput v-model="c.saturday" type="text" :disabled="isSafe4" :field_name="i === 0 ? $t('alarm_create.saturday') : ''" :placeholder="$t('alarm_create.saturday')" />
        </div>
        <div class="w-full md:w-1/12 px-2 py-1">
          <BaseInput v-model="c.sunday" type="text" :disabled="isSafe4" :field_name="i === 0 ? $t('alarm_create.sunday_holiday') : ''" :placeholder="$t('alarm_create.sunday_holiday')" />
        </div>
      </div>
    </div>

    <!-- alarm-holder user accounts: accounts that already exist (from the order the
         details came from) are shown, not created again; new rows are created -->
    <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
    <div class="flex flex-wrap mt-4">
      <div class="w-full text-gray-600 text-md font-bold px-2 pb-1 font-serif">
        {{ $t('admin_order_alarm.user_account_title') }}
      </div>
      <div class="w-full px-2 pb-2">
        <label class="mr-6 text-sm text-gray-800 cursor-pointer">
          <input type="radio" class="mr-1" name="od-account-wanted" :value="true" v-model="accountWanted" :disabled="hasCreatedAccount" />
          {{ $t('yes') }}
        </label>
        <label class="text-sm text-gray-800 cursor-pointer">
          <input type="radio" class="mr-1" name="od-account-wanted" :value="false" v-model="accountWanted" :disabled="hasCreatedAccount" />
          {{ $t('no') }}
        </label>
      </div>
      <template v-if="accountWanted">
        <div class="w-full" v-for="(account, i) in form.user_accounts" :key="`od-account-${i}`">
          <div class="w-full px-2 pt-2 text-sm text-green-700 font-semibold" v-if="account.status === 'created'">
            {{ $t('admin_order_alarm.account_created', { username: account.username }) }}
          </div>
          <div class="w-full flex items-start">
            <div class="flex-grow flex flex-wrap">
              <div class="w-full md:w-1/4 px-2 py-2">
                <BaseInput v-model="account.firstname" :vid="`od-account-firstname-${i}`" type="text" :field_name="$t('create_user_page.first_name')" rules="required" :readonly="account.status === 'created'" :disabled="account.status === 'created'" />
              </div>
              <div class="w-full md:w-1/4 px-2 py-2">
                <BaseInput v-model="account.lastname" :vid="`od-account-lastname-${i}`" type="text" :field_name="$t('create_user_page.surname')" rules="required" :readonly="account.status === 'created'" :disabled="account.status === 'created'" />
              </div>
              <div class="w-full md:w-1/4 px-2 py-2">
                <BaseInput v-model.trim="account.email" :vid="`od-account-email-${i}`" type="text" :field_name="$t('create_user_page.email')" :rules="accountEmailRules(i)" :readonly="account.status === 'created'" :disabled="account.status === 'created'" />
              </div>
              <div class="w-full md:w-1/4 px-2 py-2">
                <BaseInput v-model="account.phone" :vid="`od-account-phone-${i}`" type="text" :field_name="$t('create_user_page.phone')" :readonly="account.status === 'created'" :disabled="account.status === 'created'" />
              </div>
            </div>
            <button v-if="form.user_accounts.length > 1 && account.status !== 'created'" class="text-red-500 px-2 mt-10" @click.prevent="removeAccountRow(i)">
              <BaseIcon icon="minus-circle" />
            </button>
          </div>
        </div>
        <div class="w-full px-2 py-2">
          <button class="btn-blue-outline px-3 py-2" @click.prevent="addAccountRow()">
            {{ $t('admin_order_alarm.account_add') }} <BaseIcon icon="plus" class="ml-1" />
          </button>
        </div>
      </template>
    </div>

    <div class="flex flex-wrap mt-4">
      <div class="w-full px-2 py-2">
        <BaseTextarea rows="5" v-model="form.comment" vid="od-comment" :field_name="$t('admin_order_alarm.comment_order_handling')" />
      </div>
    </div>

    <!-- the alarm plan: personal/group, risk profile, address and the personal details -->
    <div class="w-full mx-2 border-b border-secondary-400 my-5"></div>
    <div class="flex flex-wrap mt-4">
      <div class="w-full md:w-1/3 px-2 py-2">
        <BaseSelect v-model="form.selected_alarm_sort" vid="od-alarm-sort" :options="safe4AlarmSortChoices(alarmSortOptions, isSafe4)" nameSelector="label" optionSelector="value" rules="required" :field_name="$t('alarm_create.personal_group_alarm')" />
      </div>
      <!-- a personal order answers these per person, in the blocks below -->
      <div class="w-full md:w-1/3 px-2 py-2" v-if="!isPersonalAlarm">
        <BaseSelect v-model="form.risk_profile" vid="od-risk-profile" :options="showMalmoRiskProfiles ? riskProfileOptionsMSL : riskProfileOptions" nameSelector="label" optionSelector="value" rules="required" :field_name="$t('admin_order_alarm.risk_profile')" />
      </div>
      <div class="w-full md:w-1/3 px-2 py-2" v-if="!isPersonalAlarm && !isSafe4">
        <BaseInput v-model="form.from_address" vid="od-from-address" type="text" rules="required" :field_name="$t('admin_order_alarm.from_address')" />
      </div>
    </div>
    <!-- Safe-4: the address split into street / postal code / city -->
    <div class="flex flex-wrap" v-if="isSafe4 && !isPersonalAlarm">
      <div class="w-full md:w-1/3 px-2 py-2">
        <BaseInput v-model="form.from_address_parts.street" type="text" rules="required|max:30" maxlength="30" :field_name="$t('safe4_form.street')" />
      </div>
      <div class="w-full md:w-1/3 px-2 py-2">
        <BaseInput v-model="form.from_address_parts.zip" type="text" :rules="`required|safe4_postcode:${country}`" :field_name="$t('safe4_form.postal_code')" />
      </div>
      <div class="w-full md:w-1/3 px-2 py-2">
        <BaseInput v-model="form.from_address_parts.city" type="text" rules="required" :field_name="$t('safe4_form.city')" />
      </div>
    </div>

    <div class="flex flex-wrap" v-if="riskProfileCode === 'threat' || riskProfileCode === 'assault'">
      <div class="w-full md:w-1/3 px-2 py-2">
        <p class="text-xs italic text-gray-500 mb-1">{{ $t('admin_order_alarm.reference_number_hint') }}</p>
        <BaseInput v-model="form.reference_number" vid="od-reference-number" type="text" :field_name="$t('admin_order_alarm.reference_number')" />
      </div>
    </div>

    <!-- a personal alarm: one block per person, up to the number of alarm devices -->
    <template v-if="isPersonalAlarm">
      <div class="w-full px-2 pt-2 flex items-center justify-between">
        <span class="text-gray-600 text-sm font-bold font-serif">{{ $t('admin_order_alarm.personal_details') }}</span>
        <span class="text-xs text-gray-500">{{ $t('admin_order_alarm.personal_details_count', { used: form.personal_alarms.length, total: maxPersonalAlarmBlocks }) }}</span>
      </div>
      <p class="w-full px-2 pb-2 text-xs italic text-gray-500">{{ $t('admin_order_alarm.personal_details_hint') }}</p>

      <div class="w-full px-2 pb-3" v-for="(person, i) in form.personal_alarms" :key="`od-personal-${i}`">
        <div class="border border-gray-200 rounded">
          <div class="flex items-center justify-between bg-gray-50 px-3 py-2 cursor-pointer" @click="togglePersonalBlock(i)">
            <span class="text-sm font-semibold text-gray-800">
              {{ $t('admin_order_alarm.personal_person', { n: i + 1 }) }}
              <span class="text-xs font-normal text-gray-500 ml-2" v-if="person.name">— {{ person.name }}</span>
            </span>
            <span class="flex items-center">
              <button v-if="form.personal_alarms.length > 1" class="text-red-500 mr-3" @click.stop.prevent="removePersonalBlock(i)">
                <BaseIcon icon="minus-circle" />
              </button>
              <span class="text-gray-500 text-xs">{{ collapsedPersonalBlocks[i] ? '▼' : '▲' }}</span>
            </span>
          </div>

          <div class="flex flex-wrap p-2" v-show="!collapsedPersonalBlocks[i]">
            <div class="w-full md:w-1/3 px-2 py-2">
              <div class="flex items-end">
                <div class="flex-grow">
                  <BaseSelect v-model="person.risk_profile" :options="showMalmoRiskProfiles ? riskProfileOptionsMSL : riskProfileOptions" :vid="`od-person-risk-${i}`" nameSelector="label" optionSelector="value" rules="required" :field_name="$t('admin_order_alarm.risk_profile')" />
                </div>
                <button v-if="form.personal_alarms.length > 1" type="button" class="ml-1 mb-3 px-2 py-1.5 text-blue-500 border border-blue-300 rounded hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" :disabled="!person.risk_profile" :title="$t('order_devices.apply_to_all')" @click.prevent="applyPersonFieldToAll('risk_profile', i)">
                  <BaseIcon icon="copy" />
                </button>
              </div>
            </div>
            <div class="w-full md:w-1/3 px-2 py-2">
              <div class="flex items-end">
                <div class="flex-grow">
                  <BaseInput v-model="person.from_address" :vid="`od-person-from-${i}`" type="text" rules="required" :field_name="$t('admin_order_alarm.from_address')" />
                </div>
                <button v-if="form.personal_alarms.length > 1" type="button" class="ml-1 mb-3 px-2 py-1.5 text-blue-500 border border-blue-300 rounded hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" :disabled="!(person.from_address || '').trim()" :title="$t('order_devices.apply_to_all')" @click.prevent="applyPersonFieldToAll('from_address', i)">
                  <BaseIcon icon="copy" />
                </button>
              </div>
            </div>
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseInput v-model="person.reporting" :vid="`od-person-reporting-${i}`" type="text" :field_name="$t('alarm_create.reporting')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseInput v-model="person.name" :vid="`od-person-name-${i}`" type="text" :field_name="$t('alarm_create.name')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseInput v-model="person.home_address" :vid="`od-person-home-${i}`" type="text" :field_name="$t('alarm_create.home_address')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2">
              <BaseSelect v-model="person.transport" :vid="`od-person-transport-${i}`" :options="transportOptions" nameSelector="label" optionSelector="value" :field_name="$t('alarm_create.transport')" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2" v-if="person.transport === 'car'">
              <BaseInput v-model="person.registration_number" :vid="`od-person-reg-${i}`" type="text" :field_name="$t('alarm_create.registration_number')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2" v-if="person.transport === 'car'">
              <BaseInput v-model="person.model" :vid="`od-person-model-${i}`" type="text" :field_name="$t('alarm_create.model')" rules="required" />
            </div>
            <div class="w-full md:w-1/3 px-2 py-2" v-if="person.transport === 'car'">
              <BaseInput v-model="person.color" :vid="`od-person-color-${i}`" type="text" :field_name="$t('alarm_create.color')" rules="required" />
            </div>
            <div class="w-full px-2 py-2">
              <BaseTextarea rows="2" v-model="person.alarms_name" :vid="`od-person-names-${i}`" :field_name="$t('admin_order_alarm.product_names')" rules="required" />
            </div>
            <div class="w-full md:w-1/2 px-2 py-2">
              <BaseInput v-model="person.alarm_password" :vid="`od-person-password-${i}`" type="text" :field_name="$t('admin_order_alarm.password')" rules="required" maxlength="200" />
              <p class="text-xs italic text-gray-500 mt-1">{{ $t('admin_order_alarm.password_hint') }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="w-full px-2 pb-3">
        <button v-if="canAddPersonalBlock" class="btn-blue-outline px-3 py-2" @click.prevent="addPersonalBlock()">
          {{ $t('admin_order_alarm.personal_add') }} <BaseIcon icon="plus" class="ml-1" />
        </button>
        <p v-else class="text-xs italic text-gray-500">{{ $t('admin_order_alarm.personal_add_limit', { total: maxPersonalAlarmBlocks }) }}</p>
      </div>
    </template>

    <div class="flex flex-wrap" v-if="!isPersonalAlarm">
      <div class="w-full px-2 py-2">
        <BaseTextarea rows="2" v-model="form.alarms_name" vid="od-alarms-name" :field_name="$t('admin_order_alarm.product_names')" rules="required" />
      </div>
    </div>
    <div class="flex flex-wrap" v-if="!isPersonalAlarm">
      <div class="w-full md:w-1/2 px-2 py-2">
        <BaseInput v-model="form.alarm_password" vid="alarm-password" type="text" :field_name="$t('admin_order_alarm.password')" :rules="isSafe4 ? 'required|max:20|safe4_password' : 'required'" maxlength="200" />
        <p class="text-xs italic text-gray-500 mt-1">{{ $t('admin_order_alarm.password_hint') }}</p>
      </div>
    </div>

    <!-- the action plan of the chosen risk profile, as on the order form -->
    <div class="flex flex-wrap mt-2">
      <div class="w-full px-2 text-xs italic" v-if="riskProfileCode === 'threat'">
        <h5 class="font-bold mb-1">{{ $t('alarm_create.risk_threat_primary') }}</h5>
        <p><span class="font-bold">{{ $t('alarm_create.risk_threat_mic_off') }}</span> {{ $t('alarm_create.risk_threat_primary_text') }}</p>
        <h5 class="font-bold mb-1 mt-2">{{ $t('alarm_create.risk_threat_actions') }}</h5>
        <ol>
          <li><span class="font-bold">{{ $t('alarm_create.risk_threat_step_1') }}</span> {{ $t('alarm_create.risk_threat_step_1_text') }}</li>
          <li><span class="font-bold">{{ $t('alarm_create.risk_threat_step_2') }}</span> {{ $t('alarm_create.risk_threat_step_2_text') }}</li>
          <li><span class="font-bold">{{ $t('alarm_create.risk_threat_step_3') }}</span> {{ $t('alarm_create.risk_threat_step_3_text') }}</li>
          <li>{{ $t('alarm_create.risk_threat_step_4') }}</li>
          <li><span class="font-bold">{{ $t('alarm_create.risk_threat_step_5') }}</span> {{ $t('alarm_create.risk_threat_step_5_text') }}</li>
          <li><span class="font-bold">{{ $t('alarm_create.risk_threat_step_6') }}</span> {{ $t('alarm_create.risk_threat_step_6_text') }}</li>
        </ol>
      </div>
      <div class="w-full px-2 text-xs italic" v-if="riskProfileCode === 'emergency'">
        <h5 class="font-bold mb-1">{{ $t('alarm_create.risk_emergency_primary') }}</h5>
        <p><span class="font-bold">{{ $t('alarm_create.risk_emergency_mic_on') }}</span> {{ $t('alarm_create.risk_emergency_primary_text') }}</p>
        <h5 class="font-bold mb-1 mt-2">{{ $t('alarm_create.risk_emergency_actions') }}</h5>
        <ol>
          <li><span class="font-bold">{{ $t('alarm_create.risk_emergency_step_1') }}</span> {{ $t('alarm_create.risk_emergency_step_1_text') }}</li>
          <li><span class="font-bold">{{ $t('alarm_create.risk_emergency_step_2') }}</span> {{ $t('alarm_create.risk_emergency_step_2_text') }}</li>
          <li><span class="font-bold">{{ $t('alarm_create.risk_emergency_step_3') }}</span> {{ $t('alarm_create.risk_emergency_step_3_text') }}</li>
          <li><span class="font-bold">{{ $t('alarm_create.risk_emergency_step_4') }}</span> {{ $t('alarm_create.risk_emergency_step_4_text') }}</li>
        </ol>
      </div>
      <div class="w-full px-2 text-xs italic" v-if="riskProfileCode === 'assault'">
        <h5 class="font-bold mb-1">{{ $t('alarm_create.risk_assault_primary') }}</h5>
        <p><span class="font-bold">{{ $t('alarm_create.risk_assault_mic_off') }}</span> {{ $t('alarm_create.risk_assault_primary_text') }}</p>
        <h5 class="font-bold mb-1 mt-2">{{ $t('alarm_create.risk_assault_actions') }}</h5>
        <ol>
          <li><span class="font-bold">{{ $t('alarm_create.risk_assault_step_1') }}</span> {{ $t('alarm_create.risk_assault_step_1_text') }}</li>
          <li><span class="font-bold">{{ $t('alarm_create.risk_assault_step_2') }}</span> {{ $t('alarm_create.risk_assault_step_2_text') }}</li>
          <li><span class="font-bold">{{ $t('alarm_create.risk_assault_step_3') }}</span> {{ $t('alarm_create.risk_assault_step_3_text') }}</li>
          <li><span class="font-bold">{{ $t('alarm_create.risk_assault_step_4') }}</span> {{ $t('alarm_create.risk_assault_step_4_text') }}</li>
        </ol>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import { RISK_PROFILE, ALARM_SORT, normalizeRiskProfile, normalizeAlarmSort } from "@/utils/alarmEnums";
import { splitAddressLine } from "@/utils/safe4Postcodes";

const emptyContactRow = () => ({ name: "", function: "", phone: "", weekday: "", saturday: "", sunday: "" });
// Safe-4 (SEC-364 A3): contact persons are called around the clock, the same
// value the device forms and the server use
const SAFE4_CONTACT_TIME = "00:00-23:59";
const emptyAccount = () => ({ firstname: "", lastname: "", email: "", phone: "", username: "", status: "", error: "", reason: "" });
const emptyPersonalBlock = () => ({
  reporting: "", name: "", home_address: "",
  transport: "", registration_number: "", model: "", color: "",
  risk_profile: "", from_address: "", alarms_name: "", alarm_password: "",
});
const CUSTOMER_INFO_FIELDS = [
  "contact_name", "contact_email", "contact_phone", "company_name", "invoice_detail",
  "outdoor_work", "alert_members", "indoor_tracking", "man_down_alert", "email_sms_detail",
  "total_user", "person_group_names", "work_task", "working_place", "risk_detail", "other_info",
];

export default {
  name: "OrderDetailsFields",
  props: {
    // The order to start from (e.g. the original agreement's order), or null
    initialOrder: {
      type: Object,
      default: null,
    },
    // Orderer details to use where initialOrder has none (e.g. the agreement's customer)
    defaultCustomerInfo: {
      type: Object,
      default: () => ({}),
    },
    // The chosen alarm central: Safe-4 and Malmö change the fields, as on the order form
    alarmCenterId: {
      type: String,
      default: "",
    },
    alarmCenterName: {
      type: String,
      default: "",
    },
    // se / no - Safe-4's postcode rule depends on it
    country: {
      type: String,
      default: "se",
    },
    // alarm devices on the order: one personal block per device at most
    alarmDeviceCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      form: this.formFromOrder(null),
      notificationContacts: [{ email: "", msisdn: "" }],
      accountWanted: false,
      collapsedPersonalBlocks: [],
      safe4CenterId: "",
    };
  },
  computed: {
    generalQuestions() {
      return [
        { field: "outdoor_work", label: "admin_order_alarm.outdoor_work" },
        { field: "alert_members", label: "admin_order_alarm.alert_colleagues" },
        { field: "indoor_tracking", label: "admin_order_alarm.indoor_location" },
      ];
    },
    isSafe4() {
      return !!this.safe4CenterId && this.alarmCenterId === this.safe4CenterId;
    },
    showMalmoRiskProfiles() {
      return /(Malmö Stads Larmcentral)|(Skyresponse Malmö)/.test(this.alarmCenterName || "");
    },
    riskProfileOptions() {
      return [
        { value: RISK_PROFILE.THREAT, label: this.$t("admin_order_alarm.threat_alarm") },
        { value: RISK_PROFILE.EMERGENCY, label: this.$t("admin_order_alarm.emergency_alarm") },
        { value: RISK_PROFILE.ASSAULT, label: this.$t("risk_profile.burglar_alarm") },
      ];
    },
    riskProfileOptionsMSL() {
      return [
        ...this.riskProfileOptions,
        { value: this.$t("alarm_create.malmo_fixed"), label: this.$t("alarm_create.malmo_fixed") },
        { value: this.$t("alarm_create.malmo_mobile"), label: this.$t("alarm_create.malmo_mobile") },
        { value: this.$t("alarm_create.malmo_emergency"), label: this.$t("alarm_create.malmo_emergency") },
      ];
    },
    alarmSortOptions() {
      return [
        { value: ALARM_SORT.GROUP, label: this.$t("alarm_create.group_alarm") },
        { value: ALARM_SORT.PERSONAL, label: this.$t("alarm_create.personal") },
      ];
    },
    transportOptions() {
      return [
        { value: "walking", label: this.$t("alarm_create.transport_walking") },
        { value: "bike", label: this.$t("alarm_create.transport_bike") },
        { value: "public_transport", label: this.$t("alarm_create.transport_public_transport") },
        { value: "car", label: this.$t("alarm_create.transport_car") },
      ];
    },
    alarmOptionCheckboxes() {
      return [
        { field: "call_button", label: this.$t("admin_order_alarm.internal_alarm") },
        { field: "man_down", label: this.$t("admin_order_alarm.man_down") },
        { field: "timer_alarm", label: this.$t("admin_order_alarm.timer_alarm") },
      ];
    },
    deliveryMethodOptions() {
      return [
        { value: "salesperson", name: this.$t("admin_order_alarm.delivery_to_salesperson") },
        { value: "customer_pickup", name: this.$t("admin_order_alarm.delivery_customer_pickup") },
        { value: "recipient", name: this.$t("admin_order_alarm.delivery_to_recipient") },
      ];
    },
    isDeliveryToRecipient() {
      return this.form.delivery_method === "recipient";
    },
    riskProfileCode() {
      return normalizeRiskProfile(this.form.risk_profile);
    },
    isPersonalAlarm() {
      return normalizeAlarmSort(this.form.selected_alarm_sort) === ALARM_SORT.PERSONAL;
    },
    maxPersonalAlarmBlocks() {
      return Math.max(1, this.alarmDeviceCount || 0);
    },
    canAddPersonalBlock() {
      return this.form.personal_alarms.length < this.maxPersonalAlarmBlocks;
    },
    hasCreatedAccount() {
      return this.form.user_accounts.some((account) => account.status === "created");
    },
  },
  watch: {
    initialOrder: {
      immediate: true,
      handler() {
        this.reset();
      },
    },
    // Safe-4 has no personal alarms: choosing it drops that choice
    isSafe4(value) {
      if (value && this.isPersonalAlarm) this.form.selected_alarm_sort = "";
      if (value) this.applySafe4ContactTimes(); // SEC-373
    },
    // SEC-373: rows replaced (the form is rebuilt from the order) while Safe-4
    // is the central get the whole day too
    "form.contact_persons"() {
      this.applySafe4ContactTimes();
    },
    // a personal alarm always has at least the first person's block
    isPersonalAlarm(value) {
      if (value && this.form.personal_alarms.length === 0) this.addPersonalBlock();
    },
  },
  created() {
    // Which alarm central is Safe-4. Fails quietly -> no Safe-4 behavior.
    this.axios
      .get(`${process.env.VUE_APP_SERVER_URL}/safe4/config`)
      .then((res) => { this.safe4CenterId = (res.data && res.data.safe4_alarm_center_id) || ""; })
      .catch(() => { this.safe4CenterId = ""; });
  },
  methods: {
    // Safe-4 (SEC-364 A3 / SEC-373): the time fields are locked for Safe-4, so
    // every contact person row shows what the devices get - the whole day in
    // all three - as in the device forms and the order form. Other centrals
    // keep what was typed.
    applySafe4ContactTimes() {
      if (!this.isSafe4 || !this.form) return;
      (this.form.contact_persons || []).forEach((contact) => {
        this.$set(contact, "weekday", SAFE4_CONTACT_TIME);
        this.$set(contact, "saturday", SAFE4_CONTACT_TIME);
        this.$set(contact, "sunday", SAFE4_CONTACT_TIME);
      });
    },
    // The form state from an order, with the same defaults and legacy handling as
    // the order form's setupOrderForm
    formFromOrder(order) {
      const o = order || {};
      const info = o.customer_info || {};
      const defaults = this.defaultCustomerInfo || {};
      const parts = o.from_address_parts || {};
      const hasParts = !!(parts.street || parts.zip || parts.city);
      const savedAccounts = Array.isArray(o.user_accounts) && o.user_accounts.length > 0
        ? o.user_accounts
        : (o.user_account && (o.user_account.firstname || o.user_account.email || o.user_account.status) ? [o.user_account] : []);
      const savedPeople = Array.isArray(o.personal_alarms) && o.personal_alarms.length > 0
        ? o.personal_alarms
        : (o.personal_alarm && o.personal_alarm.name ? [o.personal_alarm] : []);
      const contacts = (Array.isArray(o.contact_persons) ? o.contact_persons : []).map((c) => ({ ...emptyContactRow(), ...(c || {}) }));
      while (contacts.length < 3) contacts.push(emptyContactRow());

      return {
        customer_info: CUSTOMER_INFO_FIELDS.reduce((result, field) => {
          result[field] = info[field] || defaults[field] || "";
          return result;
        }, {}),
        notify_general: o.notify_general === true || !!o.notification_email || (o.notification_phone || []).length > 0,
        notify_individual: o.notify_individual === true || !!o.notification_individual_detail,
        notification_individual_detail: o.notification_individual_detail || "",
        notification_phone: Array.isArray(o.notification_phone) ? o.notification_phone : [],
        direct_dispatch: o.direct_dispatch === true,
        security_company: o.security_company || "",
        security_company_phone: o.security_company_phone || "",
        security_company_object_id: o.security_company_object_id || "",
        call_button: o.call_button === true,
        man_down: o.man_down === true,
        timer_alarm: o.timer_alarm === true,
        delivery_method: o.delivery_method || "",
        direct_shipping_address: o.direct_shipping_address || "",
        // the tracking link belonged to the earlier shipment
        delivery_tracking_link: "",
        contact_persons: contacts,
        user_accounts: savedAccounts.map((account) => ({ ...emptyAccount(), ...(account || {}) })),
        comment: "",
        selected_alarm_sort: normalizeAlarmSort(o.selected_alarm_sort || ""),
        risk_profile: normalizeRiskProfile(o.risk_profile),
        from_address: o.from_address || "",
        from_address_parts: hasParts
          ? { street: parts.street || "", zip: parts.zip || "", city: parts.city || "" }
          : splitAddressLine(o.from_address),
        reference_number: o.reference_number || "",
        personal_alarms: savedPeople.map((person) => ({ ...emptyPersonalBlock(), ...(person || {}) })),
        alarms_name: o.alarms_name || "",
        alarm_password: o.alarm_password || "",
      };
    },

    // Start over from initialOrder (or empty)
    reset() {
      this.form = this.formFromOrder(this.initialOrder);
      const emails = String((this.initialOrder && this.initialOrder.notification_email) || "").split(",").map((s) => s.trim()).filter(Boolean);
      const phones = this.form.notification_phone;
      const pairs = Math.max(emails.length, phones.length, 1);
      this.notificationContacts = Array.from({ length: pairs }, (_, i) => ({
        email: emails[i] || "",
        msisdn: phones[i] ? phones[i].msisdn : "",
      }));
      this.accountWanted = this.form.user_accounts.length > 0;
      if (this.form.user_accounts.length === 0) this.form.user_accounts.push(emptyAccount());
      if (this.isPersonalAlarm && this.form.personal_alarms.length === 0) this.form.personal_alarms.push(emptyPersonalBlock());
      this.collapsedPersonalBlocks = this.form.personal_alarms.map(() => false);
      if (this.$refs.orderDetailsObserver) this.$nextTick(() => this.$refs.orderDetailsObserver.reset());
    },

    // vee-validate over every field; folded person blocks are opened so an error is visible
    async validate() {
      const valid = await this.$refs.orderDetailsObserver.validate();
      if (!valid) this.collapsedPersonalBlocks = this.form.personal_alarms.map(() => false);
      return valid;
    },

    // The details as the server stores them on the order (same shape as the order form sends)
    buildPayload() {
      const f = this.form;
      const isPersonal = this.isPersonalAlarm;
      const blocks = isPersonal ? f.personal_alarms.map((person) => ({ ...person })) : [];
      const first = blocks[0] || {};

      // Safe-4: the one-line address is built from the split one; other centrals keep no parts
      let fromAddress = f.from_address;
      let fromAddressParts = { street: "", zip: "", city: "" };
      if (this.isSafe4 && !isPersonal) {
        fromAddressParts = { ...f.from_address_parts };
        fromAddress = [fromAddressParts.street, fromAddressParts.zip, fromAddressParts.city].map((v) => String(v || "").trim()).filter(Boolean).join(" ");
      }

      const payload = {
        customer_info: { ...f.customer_info },
        notify_general: f.notify_general === true,
        notify_individual: f.notify_individual === true,
        notification_individual_detail: f.notify_individual ? f.notification_individual_detail : "",
        ...this.buildNotificationPayload(),
        direct_dispatch: f.direct_dispatch === true,
        security_company: f.direct_dispatch ? f.security_company : "",
        security_company_phone: f.direct_dispatch ? f.security_company_phone : "",
        security_company_object_id: f.direct_dispatch ? f.security_company_object_id : "",
        call_button: f.call_button === true,
        man_down: f.man_down === true,
        timer_alarm: f.timer_alarm === true,
        nine_vib: false,
        delivery_method: f.delivery_method,
        direct_shipping: f.delivery_method === "recipient" ? "yes" : "no",
        direct_shipping_address: f.delivery_method === "recipient" ? f.direct_shipping_address : "",
        delivery_tracking_link: f.delivery_method === "recipient" ? f.delivery_tracking_link : "",
        contact_persons: f.contact_persons.map((c) => ({ ...c })),
        user_accounts: this.accountWanted
          ? f.user_accounts.map(({ firstname, lastname, email, phone }) => ({ firstname, lastname, email, phone }))
          : [],
        comment: f.comment,
        selected_alarm_sort: f.selected_alarm_sort,
        reference_number: f.reference_number,
        risk_profile: f.risk_profile,
        from_address: fromAddress,
        from_address_parts: fromAddressParts,
        alarms_name: f.alarms_name,
        alarm_password: f.alarm_password,
        personal_alarms: blocks,
        personal_alarm: { ...first },
      };
      // A personal alarm answers these per person; the first person's answers stay
      // on the order too, for everything that reads the order (as on the order form)
      if (isPersonal && blocks[0]) {
        payload.risk_profile = first.risk_profile || "";
        payload.from_address = first.from_address || "";
        payload.alarms_name = first.alarms_name || "";
        payload.alarm_password = first.alarm_password || "";
      }
      return payload;
    },

    // notification_email = comma-separated addresses; notification_phone = SecurTool
    // entries, keeping the schedule of numbers the order already had
    buildNotificationPayload() {
      if (!this.form.notify_general) return { notification_email: "", notification_phone: [] };
      const emails = this.notificationContacts.map((c) => (c.email || "").trim()).filter(Boolean);
      const existingByMsisdn = new Map(this.form.notification_phone.map((entry) => [entry.msisdn, entry]));
      const defaultDay = () => ({ start: "08:00", end: "17:00" });
      const phones = this.notificationContacts
        .map((c) => (c.msisdn || "").trim())
        .filter(Boolean)
        .map((msisdn) => existingByMsisdn.get(msisdn) || {
          msisdn,
          schedule: "allday",
          weekly_time: {
            mon: defaultDay(), tue: defaultDay(), wed: defaultDay(),
            thu: defaultDay(), fri: defaultDay(), sat: defaultDay(), sun: defaultDay(),
          },
        });
      return { notification_email: emails.join(", "), notification_phone: phones };
    },

    addNotificationContact() {
      this.notificationContacts.push({ email: "", msisdn: "" });
    },
    removeNotificationContact(i) {
      this.notificationContacts.splice(i, 1);
      if (this.notificationContacts.length === 0) this.addNotificationContact();
    },

    // name / function: required on the first row, plus Safe-4's max length
    contactRules(required, safe4Max) {
      const rules = required ? ["required"] : [];
      if (this.isSafe4) rules.push(`max:${safe4Max}`);
      return rules.join("|");
    },

    accountEmailRules(index) {
      const earlierEmails = this.form.user_accounts
        .slice(0, index)
        .map((account) => (account.email || "").trim())
        .filter(Boolean)
        .filter((email) => !email.includes("|") && !email.includes(","));
      if (earlierEmails.length === 0) return "required|email";
      return `required|email|not_used_on_order:${earlierEmails.join(",")}`;
    },
    addAccountRow() {
      this.form.user_accounts.push(emptyAccount());
    },
    removeAccountRow(index) {
      if (this.form.user_accounts.length <= 1) return;
      this.form.user_accounts.splice(index, 1);
    },

    togglePersonalBlock(index) {
      this.$set(this.collapsedPersonalBlocks, index, !this.collapsedPersonalBlocks[index]);
    },
    addPersonalBlock() {
      if (!this.canAddPersonalBlock) return;
      this.form.personal_alarms.push(emptyPersonalBlock());
      this.collapsedPersonalBlocks.push(false);
    },
    removePersonalBlock(index) {
      if (this.form.personal_alarms.length <= 1) return;
      this.form.personal_alarms.splice(index, 1);
      this.collapsedPersonalBlocks.splice(index, 1);
    },
    applyPersonFieldToAll(field, sourceIndex) {
      const source = this.form.personal_alarms[sourceIndex];
      const value = source ? source[field] : "";
      if (!String(value || "").trim()) return;
      this.form.personal_alarms.forEach((person, index) => {
        if (index === sourceIndex) return;
        this.$set(person, field, value);
      });
    },

    isNumber(evt) {
      evt = evt ? evt : window.event;
      const charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
  },
};
</script>
